/**
 * React Error Boundary components for graceful error handling
 *
 * Provides global and admin-specific error boundaries using react-error-boundary.
 * Errors are logged to console for development while showing user-friendly messages.
 *
 * Security: No error details (stack traces, implementation details) are exposed to UI.
 *
 * @module errors/boundary
 */

import type { FallbackProps } from "react-error-boundary";
import {
  ErrorBoundary as ReactErrorBoundary,
  type ErrorBoundaryProps as ReactErrorBoundaryProps,
} from "react-error-boundary";

/**
 * Fallback UI for general application errors
 *
 * Shows a centered error message with a "Try again" button to reset the boundary.
 * No error details are exposed (security requirement).
 */
function ErrorFallback({ resetErrorBoundary }: FallbackProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md rounded-lg bg-white p-8 shadow-lg">
        <h2 className="mb-4 text-2xl font-semibold text-red-600">
          Something went wrong
        </h2>
        <p className="mb-6 text-gray-600">
          An unexpected error occurred. Please try refreshing the page.
        </p>
        <button
          onClick={resetErrorBoundary}
          className="rounded bg-red-600 px-4 py-2 font-medium text-white transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

/**
 * Global error boundary for general application errors
 *
 * Catches rendering errors in the component tree and displays
 * a user-friendly fallback UI.
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export function ErrorBoundary({
  children,
  onError,
}: Omit<ReactErrorBoundaryProps, "fallbackComponent">) {
  const handleError = (_error: unknown, errorInfo: React.ErrorInfo) => {
    console.error("Error caught by boundary:", errorInfo);
    onError?.(_error, errorInfo);
  };

  return (
    <ReactErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
      {children}
    </ReactErrorBoundary>
  );
}

/**
 * Fallback UI for admin panel errors
 *
 * Similar to ErrorFallback but with admin-specific messaging and
 * darker background to distinguish from public-facing errors.
 */
function AdminFallback({ resetErrorBoundary }: FallbackProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4">
      <div className="max-w-md rounded-lg bg-white p-8 shadow-lg">
        <h2 className="mb-4 text-2xl font-semibold text-red-600">
          Admin Error
        </h2>
        <p className="mb-6 text-gray-600">
          An error occurred in the admin panel. Your changes may not have been
          saved.
        </p>
        <button
          onClick={resetErrorBoundary}
          className="rounded bg-red-600 px-4 py-2 font-medium text-white transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          Reload admin panel
        </button>
      </div>
    </div>
  );
}

/**
 * Admin-specific error boundary
 *
 * Catches rendering errors in the admin panel with specialized messaging.
 * Use this boundary for admin routes to provide context-aware error handling.
 *
 * @example
 * ```tsx
 * <AdminErrorBoundary>
 *   <AdminPanel />
 * </AdminErrorBoundary>
 * ```
 */
export function AdminErrorBoundary({
  children,
  onError,
}: Omit<ReactErrorBoundaryProps, "fallbackComponent">) {
  const handleError = (_error: unknown, errorInfo: React.ErrorInfo) => {
    console.error("Admin error caught by boundary:", errorInfo);
    onError?.(_error, errorInfo);
  };

  return (
    <ReactErrorBoundary FallbackComponent={AdminFallback} onError={handleError}>
      {children}
    </ReactErrorBoundary>
  );
}
